import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AuditLogTableQueryDataRequest } from "gen/model/auditLogTableQueryDataRequest";
import { GenericTableData } from "gen/model/genericTableData";
import { isNil } from "lodash-es";
import { useCallback } from "react";
import { useDataTableServerSideSource } from "shared/components/table/data/DataTableServerSideSource";

type StartEndRow = {
  startRow: number | null;
  endRow: number | null;
  isColumnsOnly?: never;
};

type ColumnsOnly = {
  startRow?: never;
  endRow?: never;
  isColumnsOnly: boolean;
};

type GetLogParams = StartEndRow | ColumnsOnly;

export type GetLogs = (params: GetLogParams) => Promise<GenericTableData>;

export const useAuditTablePaginate = ({
  getLogs,
  queryKey,
  filters,
}: {
  getLogs: GetLogs;
  queryKey: string;
  filters: AuditLogTableQueryDataRequest;
}) => {
  const { data: tableData } = useQuery({
    queryKey: [queryKey, filters],
    queryFn: async () =>
      getLogs({
        isColumnsOnly: true,
      }),
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const getNextRows: Parameters<typeof useDataTableServerSideSource>[0] =
    useCallback(
      async ({ start_row, end_row }) => {
        // if start_row or end_row is undefined, fetch all data (ag grid will pass a nil value if it is fetching all data)
        // this should not happen as we are using server side pagination in this case
        if (isNil(start_row) || isNil(end_row)) {
          const allResponse = await getLogs({
            startRow: null,
            endRow: null,
          });
          return { table_data: allResponse };
        }

        const response = await getLogs({
          startRow: start_row,
          endRow: end_row,
        });

        return { table_data: response };
      },
      [getLogs],
    );

  const serverSideDatasource = useDataTableServerSideSource(getNextRows);
  return { tableData, serverSideDatasource };
};
